import React, {useCallback, useRef} from 'react';
import {useParams} from 'react-router-dom';

import {STACKOVERFLOW_MESSAGE} from 'src/constants';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {deleteAccessRightProfile} from 'src/openapi/admin/apis/DeleteAccessRightProfileApi';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';

import {RemoveRoleNotification} from './remove-role-notification';

import type {BackgroundTask} from 'src/contexts/background-task-context/useBackgroundTaskManager';

type Props = {
    profileId: number,
    onClose: () => void,
    profileName: string,
    onDelete: () => void,
};
export const ConfirmDeleteRoleModal = (props: Props) => {
    const {profileId, profileName, onDelete, onClose} = props;
    const {roleId} = useParams<{roleId?: string}>();

    const appDispatch = useAppDispatch();
    const createBackgroundTask = useBackgroundTaskManager();

    const {roleDelete} = useEventTracking();

    const backgroundTask = useRef<BackgroundTask | null>(null);

    const deleteRole = useCallback(() => {
        if (onClose) {
            onClose();
        }

        backgroundTask.current = createBackgroundTask(
            'remove-role',
            <RemoveRoleNotification
                profileName={profileName}
                onUndo={() => {
                    backgroundTask.current?.onClose();
                    backgroundTask.current?.completeSuccess(
                        <span>Role <b>{profileName}</b> has not been deleted.</span>,
                    );
                }}
                onConfirm={async () => {
                    roleDelete({place: roleId ? 'Role Profile' : 'Roles page'});
                    const res = await appDispatch(deleteAccessRightProfile({profileId}));
                    if (res.payload.success) {
                        onDelete();
                        backgroundTask.current?.completeSuccess(
                            <span>Role <b>{profileName}</b> has been deleted.</span>,
                        );
                    } else {
                        backgroundTask.current?.completeFailure(<span>Something went wrong.</span>);
                    }
                }}
            />,
            {
                maxStack: 1,
                stackOverflowMessage: STACKOVERFLOW_MESSAGE,
            },
        );
    }, [profileId, roleId, onDelete, appDispatch, createBackgroundTask, onClose, profileName, roleDelete]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Delete Role
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                Are you sure you want to delete this Role?
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button variant="Type.PRIMARY" onClick={deleteRole}>Delete</Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>Cancel</Button>
                </Group>
            </DialogFooter>
        </>
    );
};
